import { CloseOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import { Button, Col, Modal, Row } from 'antd';
import Title from 'antd/lib/typography/Title';
import useAuthStore from '../../../store/authStore';
import useHomeStore from '../../../store/homeStore';
import CartTable from './CartTable';
import ClearCartButton from './ClearCartButton';
import useDeleteCartItem from './hooks/useDeleteCartItem';
import useExtractionStatus from './hooks/useExtractionStatus';
import useShoppingCartData from './hooks/useShoppingCartData';
import styles from './ShoppingCart.module.scss';

export interface CartProp {
  close: () => void;
  checkout: () => void;
}

export default function ShoppingCart(props: CartProp) {
  const shouldShowCompanyWarning = useAuthStore(
    (state) => state.shouldShowCompanyWarning
  );
  const showCart = useHomeStore((state) => state.showCart);
  const setShowAssociateToCompany = useHomeStore(
    (state) => state.setShowAssociateToCompany
  );

  const {
    cartData,
    setCartData,
    shoppingPrice,
    prepareData,
    isLoading,
    shoppingCartData,
    refetchShoppingCart,
  } = useShoppingCartData();

  const isClearCartDisabled = isLoading || !cartData || cartData.length === 0;

  const { deleteItem, isDeleting } = useDeleteCartItem(
    prepareData,
    refetchShoppingCart
  );

  const {
    pendingExtractions,
    loadingCheckout,
    checkExtractionStatus,
    extractionStatusModalContextHolder,
  } = useExtractionStatus({
    prepareData,
    shoppingCartData,
    onCheckout: props.checkout,
  });

  const onTriggerCheckout = () => {
    shouldShowCompanyWarning()
      ? setShowAssociateToCompany({
          status: true,
          fun: () => checkExtractionStatus(),
        })
      : checkExtractionStatus();
  };

  const onCartCleared = () => {
    setCartData([]);
    refetchShoppingCart({ checkStatus: true, withPrice: true });
  };

  return (
    <Modal
      className={styles.cartModal}
      title='Shopping Cart'
      open={showCart}
      maskClosable={false}
      footer={null}
      onCancel={props.close}
      closeIcon={
        <span id='Cart-Close_Button' className='ant-modal-close-x'>
          <CloseOutlined />
        </span>
      }
    >
      {extractionStatusModalContextHolder}
      <ClearCartButton
        onCartCleared={onCartCleared}
        isDisabled={isClearCartDisabled}
      />
      <Row gutter={[20, 20]} wrap className={styles.cartContent}>
        <Col xs={24} lg={18} className={styles.itemsSection}>
          <Title level={4} style={{ border: 'none' }}>
            Items
          </Title>
          <CartTable
            tableData={cartData}
            delete={deleteItem}
            loading={isLoading || isDeleting}
          />
          <p style={{ margin: '7px 0' }}>
            Note: Metadata, Validations and Analytics are accessible online only
            and will not be provided by email.
          </p>
        </Col>
        <Col xs={24} lg={6} className={styles.summarySection}>
          <Title level={4}>Summary</Title>
          <Row style={{ marginTop: '20px' }}>
            <Col span={12} className={styles.sumLabel}>
              Price
            </Col>
            <Col span={12} className={styles.sumPay}>
              <span className={styles.payUnit}>€</span>
              <span data-testid='Cart-Price_Value'>
                {shoppingPrice.totalPrice}
              </span>
            </Col>
          </Row>
          <Row>
            <Col span={12} className={styles.sumLabel}>
              Discount
            </Col>
            <Col span={12} className={styles.sumPay}>
              <span className={styles.payUnit}>-€</span>
              {shoppingPrice.discount}
            </Col>
          </Row>
          <Row style={{ marginTop: '35px', fontSize: '18px' }}>
            <Col span={12} className={styles.sumLabel}>
              <b>Total</b>
            </Col>
            <Col span={12} className={styles.sumPay}>
              <b>
                <span className={styles.payUnit}>€</span>
                <span id='Cart-TotalPrice'>{shoppingPrice.subtotal}</span>
              </b>
            </Col>
          </Row>

          <Button
            id='Cart-Checkout_Button'
            disabled={
              isLoading || isDeleting || !cartData || cartData.length === 0
            }
            className={styles.cartButton}
            type='primary'
            onClick={onTriggerCheckout}
            icon={<ShoppingCartOutlined style={{ fontSize: '16px' }} />}
            loading={pendingExtractions || loadingCheckout}
          >
            PROCEED TO CHECKOUT
          </Button>
        </Col>
      </Row>
    </Modal>
  );
}
